import React from 'react';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useInsuranceEnabled } from '../../hooks/useInsuranceEnabled';

type DomainTab = {
  label: string;
  path: string;
};

function DomainLayout({
  title,
  description,
  tabs,
}: {
  title: string;
  description: string;
  tabs: DomainTab[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Most specific match wins so nested tabs (e.g. /billing/insurance/exceptions) highlight themselves.
  const activeTab = Math.max(
    tabs.reduce((best, { path }, index) => {
      const matches = location.pathname === path || location.pathname.startsWith(`${path}/`);
      return matches && (best < 0 || path.length > tabs[best].path.length) ? index : best;
    }, -1),
    0,
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
      {tabs.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => navigate(tabs[value].path)}
          variant="scrollable"
          scrollButtons={false}
          aria-label={`${title} sections`}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab) => <Tab key={tab.path} label={tab.label} />)}
        </Tabs>
      )}
      <Outlet />
    </Box>
  );
}

export function BillingInsuranceLayout() {
  const insuranceEnabled = useInsuranceEnabled();
  if (!insuranceEnabled) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" component="h1" fontWeight={700}>Insurance</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Insurance billing is not enabled for this organization.
        </Typography>
      </Box>
    );
  }

  return (
    <DomainLayout
      title="Insurance"
      description="Work payer claims, remittances, payments, and exceptions without creating a second financial ledger."
      tabs={[
        { label: 'Claims', path: '/billing/insurance' },
        { label: 'ERA / EOB & payer payments', path: '/billing/insurance/era' },
        { label: 'Exceptions', path: '/billing/insurance/exceptions' },
      ]}
    />
  );
}

export function BillingEstimatesLayout() {
  return (
    <DomainLayout
      title="Good Faith Estimates"
      description="Prepare client-facing estimates from the existing canonical workflows."
      tabs={[{ label: 'Good Faith Estimates', path: '/billing/estimates' }]}
    />
  );
}

export function BillingReportsLayout() {
  return (
    <DomainLayout
      title="Reports"
      description="Read-only financial analysis using the same canonical definitions as operational Billing."
      tabs={[
        { label: 'Revenue & production', path: '/billing/reports' },
        { label: 'Reconciliation', path: '/billing/reports/reconciliation' },
      ]}
    />
  );
}

export function BillingReportsIntro() {
  const navigate = useNavigate();
  return (
    <Stack spacing={2}>
      <Typography variant="h6" component="h2">Revenue & production</Typography>
      <Typography variant="body2" color="text.secondary">
        Provider financial reporting stays here because it explains canonical Billing data; provider performance and capacity remain outside Billing.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/billing/reports/revenue')}>
        Open revenue report
      </Button>
    </Stack>
  );
}
